import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
  useMemo,
} from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
  deleteField,
  getDoc,
} from "firebase/firestore";
import { useTheme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Snackbar from "@mui/material/Snackbar";

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import MicIcon from "@mui/icons-material/Mic";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import Paper from "@mui/material/Paper";
import MobileStepper from "@mui/material/MobileStepper";

import { db, auth } from "../../firebase/firebase";
import { PointsVisibilityContext } from "../../context/PointsVisibilityContext"; // Added
import { NodeButtonsContext } from "../../context/NodeButtonsContext";
import {
  sendMessageToChatGPT,
  sendMessageToCoordinator,
  sendAudioToGemini,
  routeAndProcessMessage,
} from "./api/geminiClient";
import { formatTimestamp } from "../../utils/timeUtils";
import { fetchTtsUrl } from "../../utils/ttsBackend";
import { useCoordinatorPlan } from "./hooks/useCoordinatorPlan";
// import { AudioRecorder } from "./AudioRecorder";

import "./ChatBox.css";

// Utility to slugify goal names for agent IDs
function slug(str) {
  return String(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

const SNOOZE_OPTIONS = [
  { label: "3h", hours: 3 },
  { label: "24h", hours: 24 },
  { label: "3d", hours: 72 },
];

const formatSnoozeDuration = (hours) => {
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const days = hours / 24;
  return `${days} day${days === 1 ? "" : "s"}`;
};

export const ChatBox = ({
  uid,
  agents,
  onGainOrLosePoints,
  onShowImprovement,
  onApplyImprovement,
  onGainOrLosePointsStepChange,
}) => {
  const theme = useTheme();

  const [displayAgents, setDisplayAgents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  // State for initial aspiration input and controlling first goal creation
  const [initialAspiration, setInitialAspiration] = useState("");
  const [initialAspDone, setInitialAspDone] = useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [anchorDropDown, setAnchorDropDown] = React.useState(null);
  const [requestedNarrating, setRequestedNarrating] = useState(null); // msg.id | null
  const [playing, setPlaying] = useState(null); // msg.id | null
  const audioRef = useRef(null);
  const [improvementSteps, setImprovementSteps] = useState({}); // track MobileStepper index per message
  const [gainOrLosePointsSteps, setGainOrLosePointsSteps] = useState({}); // track MobileStepper index per message
  const [actionFeedback, setActionFeedback] = useState(null);
  const [dismissedEntries, setDismissedEntries] = useState([]);
  const [undoingProposals, setUndoingProposals] = useState({});
  const [undoneAcknowledgements, setUndoneAcknowledgements] = useState([]);
  const [undoSnackbar, setUndoSnackbar] = useState({
    open: false,
    message: "",
    acknowledgement: null,
  });
  const [snoozePreferences, setSnoozePreferences] = useState({});
  const { showPoints } = useContext(PointsVisibilityContext); // Added
  const { showNodeButtons } = useContext(NodeButtonsContext);

  const activeDismissedEntries = useMemo(() => {
    const now = Date.now();
    return dismissedEntries.filter(
      (entry) => !entry.expiresAt || entry.expiresAt > now
    );
  }, [dismissedEntries]);

  const dismissedProposalIds = useMemo(
    () => activeDismissedEntries.map((entry) => entry.id),
    [activeDismissedEntries]
  );

  const undoneAckSet = useMemo(
    () => new Set(undoneAcknowledgements),
    [undoneAcknowledgements]
  );

  const getPreferredSnoozeOptions = useCallback(
    (agentId) => {
      if (!agentId) return SNOOZE_OPTIONS;
      const last = snoozePreferences[agentId]?.lastDurationHours;
      if (!last) return SNOOZE_OPTIONS;
      const ordered = [...SNOOZE_OPTIONS];
      ordered.sort((a, b) => {
        if (a.hours === last) return -1;
        if (b.hours === last) return 1;
        return a.hours - b.hours;
      });
      return ordered;
    },
    [snoozePreferences]
  );

  const snackbarAction = undoSnackbar.acknowledgement ? (
    <Button
      color="secondary"
      size="small"
      onClick={() =>
        handleUndoCoordinatorAcknowledgement(undoSnackbar.acknowledgement)
      }
    >
      Undo
    </Button>
  ) : null;

  // Speech to text recording state
  const [isRecording, setIsRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const speechSupported =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);
  const speechRecRef = useRef(null);
  const speechTextRef = useRef("");

  const openDropDown = Boolean(anchorDropDown);

  useEffect(() => {
    const routerAgent = agents.find((a) => a.id === "coordinator_router");

    const filteredAgents = [
      {
        id: "auto",
        name: "Assistant",
        photoURL: routerAgent?.photoURL,
        task: null,
      },
      ...agents.filter(
        (a) =>
          a.id !== "coordinator_router" &&
          a.id !== "coordinator_profile" &&
          a.name !== "Coordinator-Profile"
      ),
    ];
    setDisplayAgents(filteredAgents);
  }, [agents]);

  useEffect(() => {
    if (selectedIndex >= displayAgents.length && displayAgents.length > 0) {
      setSelectedIndex(0);
    }
  }, [displayAgents, selectedIndex]);

  // After initial aspiration is set up, switch selection to the new coach agent
  useEffect(() => {
    if (initialAspDone && displayAgents.length > 1) {
      const newAgentId = slug(initialAspiration) + "_coach";
      const idx = displayAgents.findIndex((a) => a.id === newAgentId);
      if (idx >= 0) {
        setSelectedIndex(idx);
        setInitialAspDone(false);
      }
    }
  }, [displayAgents, initialAspDone, initialAspiration]);

  useEffect(() => {
    return () => {
      if (speechRecRef.current) {
        try {
          speechRecRef.current.stop();
        } catch (err) {
          // ignore
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    const loadPreferences = async () => {
      try {
        const prefRef = doc(db, "users", uid, "settings", "coordinatorPreferences");
        const snapshot = await getDoc(prefRef);
        if (!cancelled && snapshot.exists()) {
          const data = snapshot.data() || {};
          setSnoozePreferences(data.snoozeDefaults || {});
        }
      } catch (err) {
        console.warn("Failed to load snooze preferences", err);
      }
    };
    loadPreferences();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  useEffect(() => {
    // Subscribe to real-time updates from Firestore
    const messagesRef = collection(db, "messages");
    const q = query(messagesRef, where("uid", "==", uid));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messagesFromFirestore = snapshot.docs
        .map((doc) => {
          const data = doc.data();
          const improvements = data.activity_improvements || {};
          return {
            ...data,
            improvements,
            id: doc.id,
            timestamp: data.timestamp.toDate(),
          };
        })
        .sort((a, b) => b.timestamp - a.timestamp); // Sort messages descending by timestamp

      // Convert timestamp to string after sorting
      const messagesWithFormattedTimestamp = messagesFromFirestore.map(
        (msg) => ({
          ...msg,
          timestamp: msg.timestamp.toString(),
        })
      );

      setMessages(messagesWithFormattedTimestamp);

      // If the latest message has gain_or_lose_points, initialize step and notify parent
      if (
        onGainOrLosePointsStepChange &&
        messagesWithFormattedTimestamp.length > 0
      ) {
        const latestMessage = messagesWithFormattedTimestamp[0];
        if (
          latestMessage.gain_or_lose_points &&
          latestMessage.gain_or_lose_points.length > 0
        ) {
          const key = latestMessage.id || latestMessage.timestamp;
          if (gainOrLosePointsSteps[key] === undefined) {
            setGainOrLosePointsSteps((prev) => ({
              ...prev,
              [key]: 0,
            }));
            // Notify with the first gain/lose point item
            onGainOrLosePointsStepChange(latestMessage.gain_or_lose_points[0]);
          }
        }
      }
    });

    return unsubscribe; // Detach listener on unmount
  }, [uid, onGainOrLosePointsStepChange]); // Added onGainOrLosePointsStepChange to dependencies

  const {
    bannerFocus,
    digestPreview,
    capacityCaption,
    digestHistorySummary,
    getStructuredOutreachMeta,
    getSignalSeverityChip,
    queuedProposals,
  } = useCoordinatorPlan({ uid, messages, dismissedProposalIds });

  const handleUndoCoordinatorAcknowledgement = useCallback(
    async (payload) => {
      const ack =
        payload?.coordinatorAcknowledgement ||
        (payload && payload.proposalId ? payload : null);
      if (!ack?.proposalId || !uid) return;
      const { proposalId, action, summary } = ack;
      setUndoingProposals((prev) => ({ ...prev, [proposalId]: true }));
      try {
        const proposalRef = doc(db, "users", uid, "agentProposals", proposalId);
        const baseUpdate = {
          status: "queued",
          userDecision: {
            type: "undo",
            revertedAction: action,
            decidedAt: serverTimestamp(),
          },
          deliverAfter: deleteField(),
        };
        if (action === "completed") {
          baseUpdate.completedAt = deleteField();
        }
        await updateDoc(proposalRef, baseUpdate);
        setDismissedEntries((prev) =>
          prev.filter((entry) => entry.id !== proposalId)
        );
        setUndoneAcknowledgements((prev) =>
          prev.includes(proposalId) ? prev : [...prev, proposalId]
        );
        setActionFeedback(
          `Reopened "${summary || "that item"}" so the coordinator can reschedule it.`
        );
        await addDoc(collection(db, "messages"), {
          uid,
          agent: "Coordinator",
          agentDisplayName: "Coordinator",
          role: "assistant",
          content: `Understood—I've restored "${summary || "that item"}" to your active queue.`,
          timestamp: new Date(),
          coordinatorAcknowledgement: {
            proposalId,
            action: "undo",
            revertedAction: action,
            summary,
          },
        });
      } catch (err) {
        console.error("Failed to undo coordinator action", err);
        setActionFeedback("Undo failed. Please try again.");
      } finally {
        setUndoingProposals((prev) => {
          const next = { ...prev };
          delete next[proposalId];
          return next;
        });
        setUndoSnackbar({
          open: false,
          message: "",
          acknowledgement: null,
        });
      }
    },
    [uid]
  );

  useEffect(() => {
    if (!actionFeedback) return undefined;
    const timeout = setTimeout(() => setActionFeedback(null), 6000);
    return () => clearTimeout(timeout);
  }, [actionFeedback]);

  useEffect(() => {
    if (dismissedEntries.length === 0) return undefined;
    const interval = setInterval(() => {
      setDismissedEntries((prev) =>
        prev.filter((entry) => !entry.expiresAt || entry.expiresAt > Date.now())
      );
    }, 60000);
    return () => clearInterval(interval);
  }, [dismissedEntries.length]);

  const handleUndoSnackbarClose = useCallback((event, reason) => {
    if (reason === "clickaway") return;
    setUndoSnackbar({ open: false, message: "", acknowledgement: null });
  }, []);

  const persistSnoozePreference = useCallback(
    async (agentId, durationHours) => {
      if (!uid || !agentId || !Number.isFinite(durationHours)) return;
      setSnoozePreferences((prev) => ({
        ...prev,
        [agentId]: { lastDurationHours: durationHours },
      }));
      try {
        const prefRef = doc(db, "users", uid, "settings", "coordinatorPreferences");
        await setDoc(
          prefRef,
          {
            snoozeDefaults: {
              [agentId]: { lastDurationHours: durationHours },
            },
          },
          { merge: true }
        );
      } catch (err) {
        console.warn("Failed to persist snooze preference", err);
      }
    },
    [uid]
  );

  const handleProposalQuickAction = useCallback(
    async (action, item, options = {}) => {
      if (!uid || !item) return;
      const proposalId = item.id || item.proposalId;
      if (!proposalId) return;
      const agentId =
        item.agentId ||
        item.agent ||
        item.raisedByAgentId ||
        item.coordinatorAcknowledgement?.agentId ||
        item.coordinatorDelivery?.raisedByAgentId ||
        null;

      if (action === "chat") {
        const summary = item.summary || "this coordinator item";
        setNewMessage(
          `I'd like to discuss "${summary}". Please help me plan the next step. (proposalId=${proposalId})`
        );
        setActionFeedback(`Drafted a reply about "${summary}".`);
        return;
      }

      try {
        const proposalRef = doc(db, "users", uid, "agentProposals", proposalId);
        if (action === "complete") {
          await updateDoc(proposalRef, {
            status: "completed",
            completedAt: serverTimestamp(),
            userDecision: {
              type: "completed",
              decidedAt: serverTimestamp(),
            },
          });
          setActionFeedback(
            `Logged "${item.summary}" as completed. Great job following through.`
          );
          setDismissedEntries((prev) => {
            const next = prev.filter((entry) => entry.id !== proposalId);
            next.push({ id: proposalId, expiresAt: null });
            return next;
          });
          const acknowledgement = {
            proposalId,
            action: "completed",
            summary: item.summary,
            agentId,
          };
          await addDoc(collection(db, "messages"), {
            uid,
            agent: "Coordinator",
            agentDisplayName: "Coordinator",
            role: "assistant",
            content: `I've recorded your progress on "${item.summary}" and removed it from the priority queue. Thank you for taking action.`,
            timestamp: new Date(),
            coordinatorAcknowledgement: {
              proposalId,
              action: "completed",
              summary: item.summary,
              agentId,
            },
            raisedByAgentId: agentId,
          });
          setUndoSnackbar({
            open: true,
            message: `Logged "${item.summary}". Undo?`,
            acknowledgement,
          });
        } else if (action === "snooze") {
          const durationHours = options.durationHours ?? 24;
          const durationMs = durationHours * 60 * 60 * 1000;
          const snoozeUntilDate = new Date(Date.now() + durationMs);
          const snoozeTimestamp = Timestamp.fromDate(snoozeUntilDate);
          const durationLabel = options.label || formatSnoozeDuration(durationHours);
          await updateDoc(proposalRef, {
            status: "queued",
            deliverAfter: snoozeTimestamp,
            userDecision: {
              type: "snoozed",
              decidedAt: serverTimestamp(),
              until: snoozeTimestamp,
              durationHours,
            },
          });
          setActionFeedback(
            `Snoozed "${item.summary}" for ${durationLabel} so the coordinator can revisit later.`
          );
          setDismissedEntries((prev) => {
            const next = prev.filter((entry) => entry.id !== proposalId);
            next.push({ id: proposalId, expiresAt: snoozeUntilDate.getTime() });
            return next;
          });
          if (agentId) {
            persistSnoozePreference(agentId, durationHours);
          }
          const acknowledgement = {
            proposalId,
            action: "snoozed",
            summary: item.summary,
            durationHours,
            agentId,
          };
          await addDoc(collection(db, "messages"), {
            uid,
            agent: "Coordinator",
            agentDisplayName: "Coordinator",
            role: "assistant",
            content: `I've paused "${item.summary}" until ${snoozeUntilDate.toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })}. I'll surface it again when you're ready.`,
            timestamp: new Date(),
            coordinatorAcknowledgement: {
              proposalId,
              action: "snoozed",
              summary: item.summary,
              until: snoozeUntilDate.toISOString(),
              durationHours,
              agentId,
            },
            raisedByAgentId: agentId,
          });
          setUndoSnackbar({
            open: true,
            message: `Snoozed "${item.summary}" for ${durationLabel}. Undo?`,
            acknowledgement,
          });
        }
      } catch (err) {
        console.error("Failed to perform coordinator quick action", err);
        setActionFeedback("Coordinator action failed. Please try again.");
      }
    },
    [
      uid,
      setNewMessage,
      setActionFeedback,
      setUndoSnackbar,
      persistSnoozePreference,
    ]
  );

  const playMsg = async (msg) => {
    if (playing === msg.id) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    setRequestedNarrating(msg.id);
    const url = await fetchTtsUrl(msg.content, "alloy");
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => setPlaying(null);
    setPlaying(msg.id);
    audio.play();
    setRequestedNarrating(false);
  };

  const handleDropDownClick = (event, index) => {
    setSelectedIndex(index);
    setAnchorDropDown(null);
  };

  const handleDropDownClose = () => {
    setAnchorDropDown(null);
  };

  const handleNextImprovement = (key, max) => {
    setImprovementSteps((prev) => ({
      ...prev,
      [key]: Math.min((prev[key] || 0) + 1, max - 1),
    }));
  };

  const handleBackImprovement = (key) => {
    setImprovementSteps((prev) => ({
      ...prev,
      [key]: Math.max((prev[key] || 0) - 1, 0),
    }));
  };

  const handleNextGainOrLosePoint = (key, points) => {
    const newActiveStep = Math.min(
      (gainOrLosePointsSteps[key] || 0) + 1,
      points.length - 1
    );
    setGainOrLosePointsSteps((prev) => ({
      ...prev,
      [key]: newActiveStep,
    }));
    if (onGainOrLosePointsStepChange && points[newActiveStep]) {
      onGainOrLosePointsStepChange(points[newActiveStep]);
    }
  };

  const handleBackGainOrLosePoint = (key, points) => {
    const newActiveStep = Math.max((gainOrLosePointsSteps[key] || 0) - 1, 0);
    setGainOrLosePointsSteps((prev) => ({
      ...prev,
      [key]: newActiveStep,
    }));
    if (onGainOrLosePointsStepChange && points[newActiveStep]) {
      onGainOrLosePointsStepChange(points[newActiveStep]);
    }
  };

  const handleFocusNode = (key, points) => {
    const activeStep = gainOrLosePointsSteps[key] || 0;
    if (onGainOrLosePointsStepChange && points[activeStep]) {
      onGainOrLosePointsStepChange(points[activeStep]);
    }
  };

  const formatImprovement = (step) => {
    if (!step) return "";
    switch (step.type) {
      case "add":
        return `Add "${step.title}" under ${step.parent} - ${step.dailyPoints} ${step.unit}/day. ${step.rationale}`;
      case "modify":
        return `Modify "${step.old_title}" under ${step.old_parent} -> "${step.new_title}" under ${step.new_parent}, ${step.dailyPoints} ${step.unit}/day. ${step.rationale}`;
      case "delete":
        return `Delete "${step.title}" under ${step.parent}. ${step.rationale}`;
      default:
        return JSON.stringify(step);
    }
  };

  const removeImprovementFromMessage = async (msg, step, key) => {
    const newImps = {
      add: [...(msg.improvements?.add || [])],
      modify: [...(msg.improvements?.modify || [])],
      delete: [...(msg.improvements?.delete || [])],
    };
    if (newImps[step.type]) {
      newImps[step.type].splice(step.index, 1);
    }
    try {
      await updateDoc(doc(db, "messages", msg.id), {
        activity_improvements: newImps,
      });
    } catch (err) {
      console.error("Failed to update improvements", err);
    }
    setImprovementSteps((prev) => ({ ...prev, [key]: 0 }));
  };

  const handleImplementImprovement = async (msg, step, key) => {
    // Defensive check for step type consistency
    const isPotentiallyModifyStep = step.old_title !== undefined; // formatImprovement for 'modify' uses old_title

    if (isPotentiallyModifyStep && step.type === "delete") {
      console.error(
        "CRITICAL MISMATCH: UI implies a 'modify' operation (due to presence of old_title), but step.type is 'delete'. Aborting implementation.",
        "Step details:",
        JSON.parse(JSON.stringify(step)) // Deep copy for logging
      );
      // Display an alert to the user in a real scenario, or send a message.
      // For now, just preventing the operation.
      return;
    }

    // Optional: A less critical but still useful check
    if (!isPotentiallyModifyStep && step.type === "modify") {
      console.warn(
        "POTENTIAL MISMATCH: step.type is 'modify', but UI might not reflect it (old_title is missing). Proceeding with caution.",
        "Step details:",
        JSON.parse(JSON.stringify(step)) // Deep copy for logging
      );
      // Allow to proceed but flag it. The main issue is unexpected deletion.
    }

    if (onApplyImprovement) {
      try {
        await onApplyImprovement(step);
      } catch (err) {
        console.error("Failed to apply improvement", err);
      }
    }
    await removeImprovementFromMessage(msg, step, key);
  };

  const handleRejectImprovement = async (msg, step, key) => {
    await removeImprovementFromMessage(msg, step, key);
  };

  // Start microphone recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = [];

      if (speechSupported) {
        const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new Rec();
        recognition.lang = "en-US";
        recognition.interimResults = false;
        speechTextRef.current = "";
        recognition.onresult = (e) => {
          speechTextRef.current = Array.from(e.results)
            .map((r) => r[0].transcript)
            .join(" ");
        };
        recognition.onerror = (e) => {
          console.error("Speech recognition error", e);
        };
        speechRecRef.current = recognition;
        try {
          recognition.start();
        } catch (err) {
          console.error("Failed to start speech recognition", err);
        }
      }

      // Ensure the MediaRecorder uses a format supported by Whisper
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => audioChunksRef.current.push(e.data);
      recorder.onstop = async () => {
        setTranscribing(true);
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        try {
          const txt = await sendAudioToGemini(audioBlob);
          const message =
            typeof txt === "string"
              ? txt
              : typeof txt?.text === "string"
              ? txt.text
              : "";
          if (message.trim()) {
            setNewMessage(message.trim());
          } else {
            console.warn("Unexpected transcription response:", txt);
            if (speechTextRef.current.trim()) {
              setNewMessage(speechTextRef.current.trim());
            }
          }
        } catch (err) {
          console.error("Audio transcription failed", err);
          if (speechTextRef.current.trim()) {
            setNewMessage(speechTextRef.current.trim());
          }
        } finally {
          setTranscribing(false);
          speechTextRef.current = "";
        }
      };
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Failed to start recording", err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== "inactive") {
        // Ensure any remaining data is flushed before stopping
        try {
          mediaRecorderRef.current.requestData();
        } catch (err) {
          // some browsers do not implement requestData()
        }
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    }
    if (speechRecRef.current) {
      try {
        speechRecRef.current.stop();
      } catch (err) {
        // ignore
      }
    }
  };

  // Handle initial aspiration submission to create first goal and agent, then send message
  const handleInitialAspirationSubmit = async () => {
    const raw = initialAspiration.trim();
    if (!raw) return;
    const cap = raw.charAt(0).toUpperCase() + raw.slice(1);
    setLoading(true);
    try {
      await addDoc(collection(db, "progress"), {
        title: cap,
        parent: "Life",
        points: 0,
        uid,
        unit: "points",
        agent: slug(cap) + "_coach",
      });
      // Delegate agent doc creation (with avatar & prompts) to backend autoDraftCoach
      await setDoc(
        doc(db, "users", uid, "agents", slug(cap) + "_coach"),
        {
          name: `${cap} Coach`,
          role: "coach",
          voice: "nova",
          task: cap,
          activatedAt: serverTimestamp(),
          draft: true,
        },
        { merge: true }
      );
      setInitialAspDone(true);
      setInitialAspiration("");
    } catch (err) {
      console.error("Error setting up initial aspiration:", err);
    } finally {
      setLoading(false);
    }
    // Send the initial user message via coordinator routing
    await handleSendMessage(cap);
  };

  /** send text or transcribed audio **/
  const handleSendMessage = async (plainText = "") => {
    if (loading) return;
    const body = plainText || newMessage.trim();
    if (!body) return;

    const agent = displayAgents[selectedIndex];
    if (!agent) return;
    setLoading(true);
    setNewMessage("");

    try {
      if (
        agent.id === "auto" ||
        (agent.role === "coordinator" && agent.id === "coordinator_router")
      ) {
        const response = await routeAndProcessMessage(body);

        if (response.routingError) {
          const userMsg = {
            uid,
            role: "user",
            agent: agent.name,
            photoURL: agent.photoURL,
            content: body,
            timestamp: new Date(),
          };
          const assistantMsg = {
            uid,
            role: "assistant",
            agent: "System",
            photoURL: agent.photoURL,
            content: response.message,
            timestamp: new Date(),
            improvements: response.improvements,
          };
          setMessages((msgs) => [assistantMsg, userMsg, ...msgs]);
          setLoading(false);
          return;
        }

        const routedAgent = displayAgents.find(
          (a) => a.id === response.agentId
        ) || {
          name: response.agentId,
          photoURL: agent.photoURL,
        };

        const userMsg = {
          uid,
          role: "user",
          agent: routedAgent.name,
          photoURL: routedAgent.photoURL,
          content: body,
          timestamp: new Date(),
        };

        const assistantMsg = response?.message && {
          uid,
          role: "assistant",
          agent: routedAgent.name,
          photoURL: routedAgent.photoURL,
          content: response.message,
          timestamp: new Date(),
          improvements: response.improvements,
        };

        setMessages((msgs) => [assistantMsg, userMsg, ...msgs].filter(Boolean));
        if (onGainOrLosePoints) {
          onGainOrLosePoints(response.gain_or_lose_points);
        }
        if (
          onGainOrLosePointsStepChange &&
          response.gain_or_lose_points &&
          response.gain_or_lose_points.length > 0
        ) {
          onGainOrLosePointsStepChange(response.gain_or_lose_points[0]);
        }
      } else {
        let response;
        if (agent.role === "coordinator" && agent.id === "coordinator_router") {
          response = await routeAndProcessMessage(body);
          if (response.routingError) {
            const userMsg = {
              uid,
              role: "user",
              agent: agent.name,
              photoURL: agent.photoURL,
              content: body,
              timestamp: new Date(),
            };
            const assistantMsg = {
              uid,
              role: "assistant",
              agent: "System",
              photoURL: agent.photoURL,
              content: response.message,
              timestamp: new Date(),
              improvements: response.improvements,
            };
            setMessages((msgs) => [assistantMsg, userMsg, ...msgs]);
            setLoading(false);
            return;
          }

          const routedAgent = displayAgents.find(
            (a) => a.id === response.agentId
          ) || {
            name: response.agentId,
            photoURL: agent.photoURL,
          };
          const userMsg = {
            uid,
            role: "user",
            agent: routedAgent.name,
            photoURL: routedAgent.photoURL,
            content: body,
            timestamp: new Date(),
          };
          const assistantMsg = response?.message && {
            uid,
            role: "assistant",
            agent: routedAgent.name,
            photoURL: routedAgent.photoURL,
            content: response.message,
            timestamp: new Date(),
            improvements: response.improvements,
          };
          setMessages((msgs) =>
            [assistantMsg, userMsg, ...msgs].filter(Boolean)
          );
        } else {
          response =
            agent.role === "coordinator"
              ? await sendMessageToCoordinator({
                  coordinatorId: agent.id,
                  userMessage: body,
                })
              : await sendMessageToChatGPT({
                  agentId: agent.id,
                  userMessage: body,
                });
          const userMsg = {
            uid,
            role: "user",
            agent: agent.name,
            photoURL: agent.photoURL,
            content: body,
            timestamp: new Date(),
          };
          const assistantMsg = {
            uid,
            role: "assistant",
            agent: agent.name,
            photoURL: agent.photoURL,
            content: response.reply || response.message,
            timestamp: new Date(),
            improvements: response.improvements,
          };
          setMessages((msgs) => [assistantMsg, userMsg, ...msgs]);
          if (onGainOrLosePoints) {
            onGainOrLosePoints(response.gain_or_lose_points);
          }
          if (
            onGainOrLosePointsStepChange &&
            response.gain_or_lose_points &&
            response.gain_or_lose_points.length > 0
          ) {
            onGainOrLosePointsStepChange(response.gain_or_lose_points[0]);
          }
        }
        // Messages are persisted via Cloud Functions
      }
    } catch (err) {
      console.error("Chat error:", err);
      // Display the error message in the chat UI
      const userMsgOnError = {
        uid,
        role: "user",
        agent: agent.name, // Agent that was attempted
        photoURL: agent.photoURL,
        content: body, // The message user tried to send
        timestamp: new Date().toString(), // Ensure toString if not already
      };
      const errorAssistantMsg = {
        uid,
        role: "assistant", // Or 'error' if you want to style it differently
        agent: "System",
        // photoURL: provide a system/error avatar if available
        content:
          err.message || "An unexpected error occurred. Please try again.",
        timestamp: new Date().toString(), // Ensure toString if not already
      };
      setMessages((msgs) =>
        [errorAssistantMsg, userMsgOnError, ...msgs].filter(Boolean)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAudioRecordEnd = async (blob) => {
    const txt = await sendAudioToGemini(blob);
    const message =
      typeof txt === "string"
        ? txt
        : typeof txt?.text === "string"
        ? txt.text
        : "";
    if (message.trim()) {
      handleSendMessage(message);
    } else {
      console.warn("Unexpected transcription response:", txt);
      if (speechSupported && speechTextRef.current.trim()) {
        handleSendMessage(speechTextRef.current.trim());
      }
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await deleteDoc(doc(db, "messages", id));
      setMessages((msgs) => msgs.filter((m) => m.id !== id));
    } catch (err) {
      console.error("Failed to delete message", err);
    }
  };

  // Extract chat colors from theme
  const chat = theme.palette.chat;

  // handleChoiceSelection function removed

  // Utility to generate a very light color based on agent name
  function getAgentColor(agentName) {
    if (!agentName) return "#f5f5f5";
    // Simple hash to number
    let hash = 0;
    for (let i = 0; i < agentName.length; i++) {
      hash = agentName.charCodeAt(i) + ((hash << 5) - hash);
    }
    // HSL: pastel color, fixed saturation/lightness, variable hue
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 95%)`;
  }

  // Initial greeting and aspiration input for new users
  if (messages.length === 0) {
    return (
      <Box
        className="chat-container"
        sx={{
          bgcolor: chat.container.background,
          color: chat.container.text,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="messages-list">
          <Box
            className="message assistant"
            sx={{
              backgroundColor:
                theme.palette.mode === "light"
                  ? getAgentColor("Assistant")
                  : chat.assistant.background,
              color: chat.assistant.text,
              borderRadius: 1,
              p: 1,
            }}
          >
            <div
              className="message-header"
              style={{ display: "flex", alignItems: "center" }}
            >
              <Avatar alt="Assistant" src={displayAgents[0]?.photoURL} />
              <div style={{ marginLeft: 8 }}>
                <span className="sender" style={{ color: chat.assistant.text }}>
                  Assistant:
                </span>
              </div>
            </div>
            <Typography sx={{ mt: 1 }}>
              {`Hi ${
                auth.currentUser?.displayName || ""
              }! Let's check in. Get started by completing the sentence at the bottom of this page and send it to me:`}
            </Typography>
          </Box>
        </div>
        <div className="input-area" style={{ marginLeft: "-10px" }}>
          <Typography sx={{ alignSelf: "center", mx: 1 }}>
            Today, I want to be more
          </Typography>
          <TextField
            value={initialAspiration}
            onChange={(e) => setInitialAspiration(e.target.value)}
            placeholder="aspiration"
            size="small"
            sx={{ mx: 1, flex: 1 }}
            onKeyDown={(e) =>
              e.key === "Enter" && handleInitialAspirationSubmit()
            }
          />
          <Button
            onClick={handleInitialAspirationSubmit}
            disabled={!initialAspiration.trim()}
          >
            <CheckIcon />
          </Button>
        </div>
      </Box>
    );
  }

  return (
    <>
      <Snackbar
        open={undoSnackbar.open}
        autoHideDuration={6000}
        onClose={handleUndoSnackbarClose}
        message={undoSnackbar.message}
        action={snackbarAction}
      />
      <Box
        className="chat-container"
        sx={{
          bgcolor: chat.container.background,
          color: chat.container.text,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
      <div className="messages-list">
        {(capacityCaption || bannerFocus || digestPreview) && (
          <Box sx={{ mb: 2, px: 1 }}>
            {actionFeedback && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  color: theme.palette.success.main,
                }}
              >
                {actionFeedback}
              </Typography>
            )}
            {capacityCaption && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                {capacityCaption}
              </Typography>
            )}
            {bannerFocus && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: digestPreview ? 1 : 0,
                  backgroundColor:
                    theme.palette.mode === "light"
                      ? getAgentColor("Coordinator")
                      : chat.assistant.background,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  Coordinator Spotlight
                  {bannerFocus.urgency && (
                    <Chip
                      size="small"
                      label={bannerFocus.urgency}
                      color="warning"
                    />
                  )}
                </Typography>
                {bannerFocus.summary && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {bannerFocus.summary}
                  </Typography>
                )}
                {bannerFocus.loss_if_ignored && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontWeight: 500 }}
                  >
                    Why it matters: {bannerFocus.loss_if_ignored}
                  </Typography>
                )}
                {bannerFocus.recommended_next_step && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Suggested action: {bannerFocus.recommended_next_step}
                    {bannerFocus.effort_minutes
                      ? ` (~${bannerFocus.effort_minutes} min)`
                      : ""}
                  </Typography>
                )}
              </Paper>
            )}
            {digestPreview && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  backgroundColor:
                    theme.palette.mode === "light"
                      ? getAgentColor("Coordinator Digest")
                      : chat.assistant.background,
                }}
              >
                <Typography variant="subtitle2">
                  Tonight's coordinator digest preview
                </Typography>
                {digestPreview.scheduledFor && (
                  <Typography variant="caption" color="text.secondary">
                    Scheduled for {formatTimestamp(digestPreview.scheduledFor)}
                  </Typography>
                )}
                <Box component="ul" sx={{ m: 0, pl: 2, mt: 1 }}>
                  {digestPreview.items.slice(0, 3).map((item) => (
                    <li key={item.proposalId}>
                      <Typography variant="body2">
                        {item.summary}
                        {item.loss_if_ignored
                          ? ` - ${item.loss_if_ignored}`
                          : ""}
                      </Typography>
                    </li>
                  ))}
                  {digestPreview.items.length > 3 && (
                    <li>
                      <Typography variant="body2" color="text.secondary">
                        +{digestPreview.items.length - 3} more queued for
                        digest
                      </Typography>
                    </li>
                  )}
                </Box>
              </Paper>
            )}
            {digestHistorySummary.entries.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mt: 1,
                  backgroundColor:
                    theme.palette.mode === "light"
                      ? getAgentColor("Coordinator Digest History")
                      : chat.assistant.background,
                }}
              >
                <Typography variant="subtitle2">Last coordinator digest</Typography>
                {digestHistorySummary.lastDeliveredAt && (
                  <Typography variant="caption" color="text.secondary">
                    Sent {formatTimestamp(digestHistorySummary.lastDeliveredAt)}
                  </Typography>
                )}
                <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                  {digestHistorySummary.entries.map((item) => (
                    <Box
                      key={item.proposalId}
                      sx={{
                        borderLeft: `4px solid ${
                          theme.palette.mode === "light" ? "#ddd" : "#444"
                        }`,
                        pl: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.summary}
                        </Typography>
                        {item.urgency && (
                          <Chip
                            size="small"
                            label={item.urgency}
                            color={item.urgencyChip}
                          />
                        )}
                      </Box>
                      {item.loss_if_ignored && (
                        <Typography variant="body2">
                          Risk: {item.loss_if_ignored}
                        </Typography>
                      )}
                    {item.recommended_next_step && (
                      <Typography variant="body2">
                        Next step: {item.recommended_next_step}
                        {item.effort_minutes ? ` (~${item.effort_minutes} min)` : ""}
                      </Typography>
                    )}
                    <ButtonGroup size="small" sx={{ mt: 0.5 }}>
                      <Button
                        title="Mark this as handled and thank the coordinator"
                        onClick={() => handleProposalQuickAction("complete", item)}
                      >
                        Log
                      </Button>
                      <Button
                        title="Draft a message to discuss this item"
                        onClick={() => handleProposalQuickAction("chat", item)}
                      >
                        Discuss
                      </Button>
                    </ButtonGroup>
                    <Box
                      sx={{
                        mt: 0.5,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {getPreferredSnoozeOptions(item.agentId).map((option) => (
                        <Button
                          key={`${item.proposalId}-digest-snooze-${option.label}`}
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            handleProposalQuickAction("snooze", item, {
                              durationHours: option.hours,
                              label: option.label,
                            })
                          }
                        >
                          Snooze {option.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
              </Paper>
            )}
            {queuedProposals.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mt: 1,
                  backgroundColor:
                    theme.palette.mode === "light"
                      ? getAgentColor("Coordinator Queue")
                      : chat.assistant.background,
                }}
              >
                <Typography variant="subtitle2">
                  Upcoming coordinator nudges
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Prioritized by urgency and earliest creation time.
                </Typography>
                <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                  {queuedProposals.slice(0, 3).map((item) => {
                    const queueMeta = getStructuredOutreachMeta(
                      item.structuredOutreach || item
                    );
                    return (
                      <Box
                        key={item.id}
                        sx={{
                          borderLeft: `4px solid ${
                            theme.palette.mode === "light" ? "#c5c5c5" : "#4f4f4f"
                          }`,
                          pl: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.summary}
                          </Typography>
                          {item.urgency && (
                            <Chip
                              size="small"
                              label={item.urgency}
                              color={queueMeta.urgencyChip}
                            />
                          )}
                          {item.agentId && (
                            <Chip
                              size="small"
                              label={item.agentId}
                              color="default"
                            />
                          )}
                        </Box>
                        {item.loss_if_ignored && (
                          <Typography variant="body2">
                            Risk: {item.loss_if_ignored}
                          </Typography>
                        )}
                      {item.recommended_next_step && (
                        <Typography variant="body2">
                          Next step: {item.recommended_next_step}
                          {item.effort_minutes
                            ? ` (~${item.effort_minutes} min)`
                            : ""}
                        </Typography>
                      )}
                      <ButtonGroup size="small" sx={{ mt: 0.5 }}>
                        <Button
                          title="Mark as complete"
                          onClick={() => handleProposalQuickAction("complete", item)}
                        >
                          Log
                        </Button>
                        <Button
                          title="Draft a coordinator reply"
                          onClick={() => handleProposalQuickAction("chat", item)}
                        >
                          Discuss
                        </Button>
                      </ButtonGroup>
                      <Box
                        sx={{
                          mt: 0.5,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        {getPreferredSnoozeOptions(item.agentId).map((option) => (
                          <Button
                            key={`${item.id || item.proposalId}-queue-snooze-${option.label}`}
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              handleProposalQuickAction("snooze", item, {
                                durationHours: option.hours,
                                label: option.label,
                              })
                            }
                          >
                            Snooze {option.label}
                          </Button>
                        ))}
                      </Box>
                      {item.deliverAfter && (
                        <Typography variant="caption" color="text.secondary">
                          Snoozed until {formatTimestamp(item.deliverAfter)}
                        </Typography>
                      )}
                      {item.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          Proposed {formatTimestamp(item.createdAt)}
                        </Typography>
                      )}
                      </Box>
                    );
                  })}
                  {queuedProposals.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      +{queuedProposals.length - 3} more queued in coordinator backlog
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}
          </Box>
        )}
        {messages
          .filter((m) => m.agent !== "Coordinator-Router")
          .map((msg, idx) => {
            const coordinatorDelivery = msg.coordinatorDelivery || {};
            const structuredOutreach = msg.structured_outreach || null;
            const supportingEvidence = msg.supporting_evidence || null;
            const digestItems = Array.isArray(msg.digest_items)
              ? msg.digest_items
              : [];
            const messageLines = (msg.content || "")
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean);
            const isDigestDelivery =
              coordinatorDelivery.channel === "digest" && digestItems.length > 0;
            const showStructuredOutreach =
              !!structuredOutreach && !isDigestDelivery;
            const supportingSignals = Array.isArray(
              supportingEvidence?.signals
            )
              ? supportingEvidence.signals
              : [];
            const recentHighlights = Array.isArray(
              supportingEvidence?.recent_activity_highlights
            )
              ? supportingEvidence.recent_activity_highlights
              : [];
            const outreachMeta = structuredOutreach
              ? getStructuredOutreachMeta(structuredOutreach)
              : null;
            const digestIntro = isDigestDelivery ? messageLines[0] || "" : "";
            const digestFooter = (() => {
              if (!isDigestDelivery) return "";
              const replyLine = messageLines.find((line) =>
                line.toLowerCase().startsWith("reply ")
              );
              return replyLine || "";
            })();
            const coordinatorAck = msg.coordinatorAcknowledgement;
            const canUndoAck =
              coordinatorAck &&
              (coordinatorAck.action === "completed" ||
                coordinatorAck.action === "snoozed") &&
              !undoneAckSet.has(coordinatorAck.proposalId);
            const isUndoingAck = coordinatorAck
              ? undoingProposals[coordinatorAck.proposalId]
              : false;
            return (
              <Box
              key={idx}
              className={`message ${msg.role}`}
              sx={{
                backgroundColor:
                  msg.role === "user"
                    ? chat.user.background
                    : theme.palette.mode === "light"
                    ? getAgentColor(msg.agent)
                    : chat.assistant.background,
                color:
                  msg.role === "user" ? chat.user.text : chat.assistant.text,
                borderRadius: 1,
                p: 1,
              }}
            >
              <div
                className="message-header"
                style={{ display: "flex", alignItems: "center" }}
              >
                {msg.role === "assistant" && (
                  <>
                    {msg.role === "assistant" &&
                      msg.agent === "Coordinator-Profile" &&
                      msg.lowConfidence && (
                        <Chip
                          size="small"
                          label="low conf"
                          color="warning"
                          sx={{ mr: 1 }}
                        />
                      )}
                    <Avatar
                      alt={`${
                        msg.agentDisplayName || msg.agent
                      } Profile Picture`}
                      src={msg.photoURL}
                    />
                  </>
                )}
                <div
                  className="message-info"
                  style={{
                    marginLeft: msg.role === "assistant" ? "8px" : undefined,
                  }}
                >
                  <span
                    className="sender"
                    style={{
                      color:
                        msg.role === "user"
                          ? chat.user.text
                          : chat.assistant.text,
                    }}
                  >
                    {msg.role === "user"
                      ? "You"
                      : `${msg.agentDisplayName || msg.agent}`}
                    :
                  </span>
                  <span className="timestamp">
                    {formatTimestamp(msg.timestamp)}
                  </span>
                </div>
                {msg.role === "user" && (
                  <>
                    <ArrowRightAltIcon />
                    <Avatar
                      alt={`${
                        msg.agentDisplayName || msg.agent
                      } Profile Picture`}
                      src={msg.photoURL}
                      style={{ marginLeft: "4px", marginRight: "10px" }}
                    />
                    {msg.agentDisplayName || msg.agent}
                  </>
                )}
                <div style={{ flex: 1 }} />{" "}
                {/* Spacer to push IconButton right */}
                <IconButton
                  size="small"
                  disabled={requestedNarrating === msg.id}
                  onClick={() => playMsg(msg)}
                  data-testid={
                    playing === msg.id
                      ? "message-stop-tts-button"
                      : "message-tts-button"
                  } // Dynamic testid
                >
                  {playing === msg.id ? (
                    <StopCircleIcon fontSize="small" />
                  ) : requestedNarrating === msg.id ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <VolumeUpIcon fontSize="small" />
                  )}
                </IconButton>
                {showNodeButtons && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteMessage(msg.id)}
                    data-testid="message-delete-button"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
              {/* Message bubble */}
              {showStructuredOutreach && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1,
                    my: 1,
                    backgroundColor:
                      theme.palette.mode === "light"
                        ? getAgentColor("Coordinator-Insight")
                        : chat.assistant.background,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2">
                      Coordinator Insight
                    </Typography>
                    {outreachMeta?.typeChip && (
                      <Chip
                        size="small"
                        label={outreachMeta.typeChip}
                        color="info"
                      />
                    )}
                    {structuredOutreach.urgency && (
                      <Chip
                        size="small"
                        label={`urgency: ${structuredOutreach.urgency}`}
                        color={outreachMeta.urgencyChip}
                      />
                    )}
                    {structuredOutreach.tone && (
                      <Chip
                        size="small"
                        label={`tone: ${structuredOutreach.tone}`}
                        color="default"
                      />
                    )}
                  </Box>
                  {structuredOutreach.summary && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {structuredOutreach.summary}
                    </Typography>
                  )}
                  {structuredOutreach.loss_if_ignored && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Risk: {structuredOutreach.loss_if_ignored}
                    </Typography>
                  )}
                  {structuredOutreach.recommended_next_step && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Next step: {structuredOutreach.recommended_next_step}
                      {structuredOutreach.effort_minutes
                        ? ` (~${structuredOutreach.effort_minutes} min)`
                        : ""}
                    </Typography>
                  )}
                  {supportingSignals.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Signals backing this:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {supportingSignals.map((signal, signalIdx) => (
                          <li key={`${signal.label || signalIdx}`}>
                            <Typography variant="body2">
                              {signal.label || "signal"}
                              {signal.severity && (
                                <Chip
                                  size="small"
                                  label={signal.severity}
                                  color={getSignalSeverityChip(signal)}
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Typography>
                            {signal.detail && (
                              <Typography variant="caption" color="text.secondary">
                                {signal.detail}
                              </Typography>
                            )}
                          </li>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {recentHighlights.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Recent progress:
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {recentHighlights.map((highlight, highlightIdx) => (
                          <li key={highlightIdx}>
                            <Typography variant="body2">{highlight}</Typography>
                          </li>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}

              {isDigestDelivery && digestItems.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1,
                    my: 1,
                    backgroundColor:
                      theme.palette.mode === "light"
                        ? getAgentColor("Coordinator Digest Detail")
                        : chat.assistant.background,
                  }}
                >
                  {digestIntro && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {digestIntro}
                    </Typography>
                  )}
                  <Typography variant="subtitle2">
                    Digest items queued
                  </Typography>
                  <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                    {digestItems.map((item) => {
                      const digestMeta = getStructuredOutreachMeta(item);
                      return (
                        <Box
                          key={item.proposalId}
                          sx={{
                            borderLeft: `4px solid ${
                              theme.palette.mode === "light" ? "#ccc" : "#555"
                            }`,
                            pl: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {item.summary}
                            </Typography>
                            {item.urgency && (
                              <Chip
                                size="small"
                                label={item.urgency}
                                color={digestMeta.urgencyChip}
                              />
                            )}
                          </Box>
                          {item.loss_if_ignored && (
                            <Typography variant="body2">
                              Risk: {item.loss_if_ignored}
                            </Typography>
                          )}
                          {item.recommended_next_step && (
                            <Typography variant="body2">
                              Next step: {item.recommended_next_step}
                              {item.effort_minutes
                                ? ` (~${item.effort_minutes} min)`
                                : ""}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  {digestFooter && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                      {digestFooter}
                    </Typography>
                  )}
                </Paper>
              )}

              {!isDigestDelivery &&
                messageLines.map((item, i) => (
                  <Typography key={i} variant="body2">
                    {item}
                  </Typography>
                ))}
              {isDigestDelivery && !digestItems.length && (
                msg.content.split("\n").map((item, i) => (
                  <Typography key={i} variant="body2">
                    {item}
                  </Typography>
                ))
              )}

              {/* Activity improvement suggestions */}
              {(() => {
                const steps = [
                  ...(msg.improvements?.add || []).map((s, i) => ({
                    ...s,
                    type: "add",
                    index: i,
                  })),
                  ...(msg.improvements?.modify || []).map((s, i) => ({
                    ...s,
                    type: "modify",
                    index: i,
                  })),
                  ...(msg.improvements?.delete || []).map((s, i) => ({
                    ...s,
                    type: "delete",
                    index: i,
                  })),
                ];
                const key = msg.id || msg.timestamp;
                const active = improvementSteps[key] || 0;
                if (steps.length === 0) return null;
                return (
                  <Paper
                    sx={{ mt: 1, width: "100%", position: "relative" }}
                    elevation={2}
                    data-testid="suggestion-activity-improvement-stepper" // Added data-testid
                  >
                    <IconButton
                      sx={{
                        position: "absolute",
                        bottom: theme.spacing(11.5),
                        right: theme.spacing(0.49),
                      }}
                      onClick={() =>
                        onShowImprovement && onShowImprovement(steps[active])
                      }
                      data-testid="suggestion-find-button" // Or suggestion-activity-find-button
                    >
                      <SearchIcon />
                    </IconButton>
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2">
                        {formatImprovement(steps[active])}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, px: 1 }}>
                      {/* <Button
                        size="small"
                        onClick={() =>
                          onShowImprovement && onShowImprovement(steps[active])
                        }
                      >
                        Find
                      </Button> */}
                      <Button
                        size="small"
                        color="success"
                        onClick={() =>
                          handleImplementImprovement(msg, steps[active], key)
                        }
                        data-testid="suggestion-implement-button" // Added data-testid
                      >
                        Implement
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() =>
                          handleRejectImprovement(msg, steps[active], key)
                        }
                        data-testid="suggestion-reject-button" // Added data-testid
                      >
                        Reject
                      </Button>
                    </Box>
                    <MobileStepper
                      variant="dots"
                      steps={steps.length}
                      position="static"
                      activeStep={active}
                      nextButton={
                        <Button
                          size="small"
                          onClick={() =>
                            handleNextImprovement(key, steps.length)
                          }
                          disabled={active === steps.length - 1}
                          data-testid="suggestion-stepper-next-button" // Added data-testid
                        >
                          Next
                          {theme.direction === "rtl" ? (
                            <KeyboardArrowLeft />
                          ) : (
                            <KeyboardArrowRight />
                          )}
                        </Button>
                      }
                      backButton={
                        <Button
                          size="small"
                          onClick={() => handleBackImprovement(key)}
                          disabled={active === 0}
                          data-testid="suggestion-stepper-back-button" // Added data-testid
                        >
                          {theme.direction === "rtl" ? (
                            <KeyboardArrowRight />
                          ) : (
                            <KeyboardArrowLeft />
                          )}
                          Back
                        </Button>
                      }
                    />
                  </Paper>
                );
              })()}

              {/* Gain or Lose Points Stepper */}
              {(() => {
                const points = msg.gain_or_lose_points || [];
                if (points.length === 0) return null;

                const key = msg.id || msg.timestamp;
                const activeStep = gainOrLosePointsSteps[key] || 0;
                const currentPoint = points[activeStep];

                return (
                  <Paper
                    sx={{ mt: 1, width: "100%", position: "relative" }}
                    elevation={2}
                    data-testid="suggestion-points-stepper"
                  >
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: theme.spacing(0.49),
                        right: theme.spacing(0.49),
                      }}
                      onClick={() => handleFocusNode(key, points)}
                      data-testid="suggestion-points-find-button" // Added data-testid
                    >
                      <SearchIcon />
                    </IconButton>
                    <Box sx={{ p: 1, display: "flex", alignItems: "center" }}>
                      {currentPoint.change === "gain" ? (
                        <CheckIcon
                          style={{ color: "green", marginRight: "4px" }}
                        />
                      ) : (
                        <CloseIcon
                          style={{ color: "red", marginRight: "4px" }}
                        />
                      )}
                      <Typography variant="h6" component="span">
                        {`${currentPoint.title}: ${
                          (currentPoint.change || currentPoint.type) === "gain"
                            ? "+"
                            : "-"
                        }${currentPoint.value ?? currentPoint.points ?? 10}`}
                      </Typography>
                    </Box>
                    <MobileStepper
                      variant="dots"
                      steps={points.length}
                      position="static"
                      activeStep={activeStep}
                      nextButton={
                        <Button
                          size="small"
                          onClick={() => handleNextGainOrLosePoint(key, points)}
                          disabled={activeStep === points.length - 1}
                          data-testid="suggestion-points-stepper-next-button" // Added data-testid
                        >
                          Next
                          {theme.direction === "rtl" ? (
                            <KeyboardArrowLeft />
                          ) : (
                            <KeyboardArrowRight />
                          )}
                        </Button>
                      }
                      backButton={
                        <Button
                          size="small"
                          onClick={() => handleBackGainOrLosePoint(key, points)}
                          disabled={activeStep === 0}
                          data-testid="suggestion-points-stepper-back-button" // Added data-testid
                        >
                          {theme.direction === "rtl" ? (
                            <KeyboardArrowRight />
                          ) : (
                            <KeyboardArrowLeft />
                          )}
                          Back
                        </Button>
                      }
                    />
                  </Paper>
                );
              })()}

              {playing === msg.id && <LinearProgress sx={{ mt: 1 }} />}
              {canUndoAck && (
                <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Need to revert this action?
                  </Typography>
                  <ButtonGroup size="small">
                    <Button
                      onClick={() => handleUndoCoordinatorAcknowledgement(msg)}
                      disabled={isUndoingAck}
                    >
                      {isUndoingAck ? "Undoing..." : "Undo"}
                    </Button>
                  </ButtonGroup>
                </Box>
              )}
            </Box>
          );
          })}
      </div>
      <div className="input-area" style={{ marginLeft: "-10px" }}>
        <Button
          id="basic-button"
          aria-controls={openDropDown ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={openDropDown ? "true" : undefined}
          onClick={(event) => setAnchorDropDown(event.currentTarget)}
          data-testid="chatbox-agent-selector-button" // Added data-testid
          sx={{
            bgcolor: "#e68900 !important",
            "&:hover": { bgcolor: "#c77700 !important" },
          }}
        >
          {selectedIndex < displayAgents.length ? (
            <>
              <Avatar
                alt={`${displayAgents[selectedIndex].name} Profile Picture`}
                src={displayAgents[selectedIndex].photoURL}
              />
              <span className="agent-name">
                {displayAgents[selectedIndex].name}
              </span>
            </>
          ) : (
            "Choose Agent"
          )}
        </Button>
        <Menu
          id="basic-menu"
          anchorEl={anchorDropDown}
          open={openDropDown}
          onClose={handleDropDownClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          {displayAgents.map((agent, index) => (
            <MenuItem
              key={agent.name}
              selected={index === selectedIndex}
              onClick={(event) => handleDropDownClick(event, index)}
            >
              <Avatar
                alt={`${agent.name} Profile Picture`}
                src={agent.photoURL}
              />
              <span style={{ marginLeft: "10px" }}>{agent.name}</span>
              {agent.draft && index !== 0 && (
                <Chip
                  size="small"
                  label="draft"
                  color="warning"
                  sx={{ ml: 1 }}
                />
              )}
            </MenuItem>
          ))}
        </Menu>
        {displayAgents[selectedIndex]?.draft && (
          <Button
            variant="outlined"
            color="warning"
            onClick={async () => {
              await updateDoc(
                doc(
                  db,
                  "users",
                  uid,
                  "agents",
                  displayAgents[selectedIndex].id
                ),
                { draft: false }
              );
            }}
            sx={{ mr: 1 }}
          >
            Activate
          </Button>
        )}
        <TextField
          fullWidth
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
          data-testid="chatbox-message-input" // Added data-testid
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ ml: 0, mr: -0.5 }}>
                <IconButton
                  size="small"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={transcribing}
                  data-testid={
                    isRecording
                      ? "chatbox-stop-recording-button"
                      : "chatbox-mic-button"
                  } // Dynamic testid
                  sx={{
                    color:
                      theme.palette.mode === "light"
                        ? "gray !important"
                        : "#fff",
                    backgroundColor: "transparent !important",
                    borderRadius: "50%",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                      color:
                        theme.palette.mode === "light"
                          ? "#555 !important"
                          : "#ddd",
                    },
                  }}
                >
                  {transcribing ? (
                    <CircularProgress
                      size={20}
                      data-testid="chatbox-transcribing-indicator"
                    /> // Added data-testid
                  ) : isRecording ? (
                    <StopCircleIcon />
                  ) : (
                    <MicIcon />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            m: 0,
            ml: 1,
            backgroundColor:
              theme.palette.mode === "light"
                ? "#fff"
                : theme.palette.background.paper,
            "& .MuiInputBase-root": { height: "55px", pr: 0 },
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSendMessage()}
          disabled={loading}
          data-testid="chatbox-send-button" // Added data-testid
          sx={{
            ml: 1,
            bgcolor: "#00b248 !important", // darker green
            "&:hover": { bgcolor: "#00701a !important" }, // even darker on hover
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Send"}
        </Button>
      </div>
      {/* <AudioRecorder onRecordEnd={handleAudioRecordEnd} /> */}
      </Box>
    </>
  );
};
